import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const setAuthToken = (token, tokenType = 'bearer') => {
  if (token) {
    apiClient.defaults.headers.common.Authorization = `${tokenType} ${token}`;
  } else {
    delete apiClient.defaults.headers.common.Authorization;
  }
};

const storedToken = localStorage.getItem('accessToken');
const storedTokenType = localStorage.getItem('tokenType') || 'bearer';
if (storedToken) {
  setAuthToken(storedToken, storedTokenType);
}

export const login = async (code) => {
  const response = await apiClient.post('/admin/auth/login', { code });

  if (!response.data?.access_token) {
    throw new Error('Invalid response structure');
  }

  const tokenType = response.data.token_type || 'bearer';
  localStorage.setItem('accessToken', response.data.access_token);
  localStorage.setItem('tokenType', tokenType);
  setAuthToken(response.data.access_token, tokenType);

  return response.data;
};

export const isAuthenticated = () => !!localStorage.getItem('accessToken');

export const logout = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('tokenType');
  localStorage.removeItem('username');
  setAuthToken(null);
};

export const fetchPersonas = async ({ skip = 0, limit = 24, search = '', sort = 'name', direction = 'asc' } = {}) => {
  const response = await apiClient.get('/admin/personas/', {
    params: { skip, limit, search: search || undefined, sort, direction },
  });
  return response.data;
};

export const createPersona = async (personaData) => {
  const response = await apiClient.post('/admin/personas/', personaData);
  return response.data;
};

export const updatePersona = async (personaId, updateData) => {
  const response = await apiClient.put(`/admin/personas/${personaId}`, updateData);
  return response.data;
};

export const deletePersona = async (personaId) => {
  const response = await apiClient.delete(`/admin/personas/${personaId}`);
  return response.data;
};

export const fetchUsers = async ({ skip = 0, limit = 50, search = '', sort = 'id', direction = 'asc' } = {}) => {
  const response = await apiClient.get('/admin/users/', {
    params: { skip, limit, search: search || undefined, sort, direction },
  });
  return response.data;
};

export const updateUserTariff = async (userId, tariff, durationDays) => {
  const response = await apiClient.post(`/admin/users/${userId}/set_tariff`, {
    tariff,
    duration_days: durationDays,
  });
  return response.data;
};

export const updateUserBonus = async (userId, bonus) => {
  const response = await apiClient.post(`/admin/users/${userId}/set_bonus`, { bonus });
  return response.data;
};

export const fetchAnalyticsSummary = async (days = 30) => {
  const response = await apiClient.get('/admin/analytics/summary', { params: { days } });
  return response.data;
};

export const fetchAnalyticsCharts = async (days = 30) => {
  const response = await apiClient.get('/admin/analytics/charts', { params: { days } });
  return response.data;
};

export const fetchAnalyticsDashboard = async (days = 30, inactiveDays = 0) => {
  const response = await apiClient.get('/admin/analytics/dashboard', {
    params: {
      days,
      inactive_days: inactiveDays,
    },
  });
  return response.data;
};

export const fetchTariffs = async () => {
  const response = await apiClient.get('/admin/tariffs/');
  return response.data;
};

export const createTariff = async (tariffData) => {
  const response = await apiClient.post('/admin/tariffs/', tariffData);
  return response.data;
};

export const updateTariff = async (tariffId, updateData) => {
  const response = await apiClient.put(`/admin/tariffs/${tariffId}`, updateData);
  return response.data;
};

export const deactivateTariff = async (tariffId) => {
  const response = await apiClient.delete(`/admin/tariffs/${tariffId}`);
  return response.data;
};

export const fetchFeedbacks = async ({ skip = 0, limit = 50, status = '', type = '', search = '', sort = 'created_at', direction = 'desc' } = {}) => {
  const response = await apiClient.get('/admin/feedback/', {
    params: {
      skip,
      limit,
      status: status || undefined,
      type: type || undefined,
      search: search || undefined,
      sort,
      direction,
    },
  });
  return response.data;
};

export const updateFeedbackStatus = async (feedbackId, status) => {
  const response = await apiClient.put(`/admin/feedback/${feedbackId}/status`, null, {
    params: { status },
  });
  return response.data;
};

export const fetchPaymentReconciliation = async ({ pendingMinutes = 15, limit = 50 } = {}) => {
  const response = await apiClient.get('/admin/payment-reconciliation/summary', {
    params: {
      pending_minutes: pendingMinutes,
      limit,
    },
  });
  return response.data;
};

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      logout();
      window.dispatchEvent(new Event('auth:logout'));
    }
    return Promise.reject(error);
  }
);
